import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import { getProjectSlug } from '../utils/slugify';
import ReactMarkdown from 'react-markdown';
import Loading from './Loading';
import FadeIn from '../components/FadeIn';

export default function PrivacyPolicy() {
  const { id: slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function fetchProject() {
      try {
        // First, fetch all projects to find the one matching the slug
        const projectsSnap = await getDocs(collection(db, 'development'));

        if (!isMounted) return;

        // Find project by slug
        const matchingProject = projectsSnap.docs.find(doc => {
          const projectData = doc.data();
          return getProjectSlug({ name: projectData.name }) === slug;
        });

        if (matchingProject) {
          setProject({ id: matchingProject.id, ...matchingProject.data() });
        } else {
          setProject(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project:', error);
        if (isMounted) {
          setProject(null);
          setLoading(false);
        }
      }
    }

    fetchProject();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        padding: '48px 16px',
      }}>
        <Loading />
      </div>
    );
  }

  if (!project || !project.legal_documents?.privacy_policy) {
    return (
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '48px 16px',
        textAlign: 'center',
      }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Privacy policy not found.</p>
        <Link
          to={`/${slug}`}
          style={{
            color: 'var(--color-link)',
            textDecoration: 'none',
            marginTop: '16px',
            display: 'inline-block',
          }}
        >
          ← Back to {project?.name || 'Project'}
        </Link>
      </div>
    );
  }

  const privacyPolicy = project.legal_documents.privacy_policy;
  const effectiveDate = privacyPolicy.effectiveDate?.toDate
    ? privacyPolicy.effectiveDate.toDate()
    : privacyPolicy.effectiveDate
    ? new Date(privacyPolicy.effectiveDate)
    : null;

  return (
    <FadeIn>
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '48px 16px',
    }}>
      <Link
        to={`/${slug}`}
        style={{
          color: 'var(--color-link)',
          textDecoration: 'none',
          fontSize: '0.875rem',
          display: 'inline-block',
          marginBottom: '32px',
        }}
      >
        ← Back to {project.name}
      </Link>

      <h1 style={{
        fontSize: '2.5rem',
        fontWeight: 900,
        fontFamily: 'Helvetica, Arial, sans-serif',
        margin: '0 0 16px 0',
        color: 'var(--color-text-primary)',
      }}>
        Privacy Policy
      </h1>

      <div style={{
        fontSize: '0.875rem',
        color: 'var(--color-text-secondary)',
        marginBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}>
        <div>
          <strong>App:</strong> {project.name}
        </div>
        {privacyPolicy.version && (
          <div>
            <strong>Version:</strong> {privacyPolicy.version}
          </div>
        )}
        {effectiveDate && (
          <div>
            <strong>Effective Date:</strong> {effectiveDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
        )}
      </div>

      <div style={{
        fontSize: '1rem',
        lineHeight: 1.7,
        color: 'var(--color-text-primary)',
      }}>
        {privacyPolicy.format === 'html' && privacyPolicy.content ? (
          <div
            style={{
              whiteSpace: 'normal',
            }}
            dangerouslySetInnerHTML={{ __html: privacyPolicy.content }}
          />
        ) : privacyPolicy.format === 'markdown' && privacyPolicy.content ? (
          <ReactMarkdown
            components={{
              h1: ({node, ...props}) => <h1 style={{fontSize: '2rem', fontWeight: 700, margin: '24px 0 16px 0', color: 'var(--color-text-primary)'}} {...props} />,
              h2: ({node, ...props}) => <h2 style={{fontSize: '1.5rem', fontWeight: 700, margin: '24px 0 12px 0', color: 'var(--color-text-primary)'}} {...props} />,
              h3: ({node, ...props}) => <h3 style={{fontSize: '1.25rem', fontWeight: 600, margin: '20px 0 12px 0', color: 'var(--color-text-primary)'}} {...props} />,
              p: ({node, ...props}) => <p style={{margin: '0 0 16px 0', lineHeight: 1.7}} {...props} />,
              ul: ({node, ...props}) => <ul style={{margin: '0 0 16px 0', paddingLeft: '24px'}} {...props} />,
              ol: ({node, ...props}) => <ol style={{margin: '0 0 16px 0', paddingLeft: '24px'}} {...props} />,
              li: ({node, ...props}) => <li style={{margin: '4px 0'}} {...props} />,
              a: ({node, ...props}) => <a style={{color: 'var(--color-link)', textDecoration: 'underline'}} {...props} />,
            }}
          >
            {privacyPolicy.content}
          </ReactMarkdown>
        ) : privacyPolicy.sections && privacyPolicy.sections.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {privacyPolicy.sections.map((section, index) => (
              <section key={section.id || index}>
                {section.title && (
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    margin: '0 0 12px 0',
                    color: 'var(--color-text-primary)',
                  }}>
                    {section.title}
                  </h2>
                )}
                {section.content && (
                  <div style={{
                    whiteSpace: 'pre-wrap',
                    color: 'var(--color-text-primary)',
                  }}>
                    {section.content}
                  </div>
                )}
              </section>
            ))}
          </div>
        ) : privacyPolicy.content ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {privacyPolicy.content}
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Privacy policy content not available.
          </p>
        )}
      </div>
    </div>
    </FadeIn>
  );
}
